import {Box, Stack, Typography, useTheme} from "@mui/material";
import UserProfilePicture from "../../common/icons/tmb/userProfilePicture";
import React, {ReactElement, useEffect, useState} from "react";
import {EnvironmentConfig} from "../../common/configs/environment.config";
import {FixedRoute} from "../../common/enums/route.enum";
import {Celebrity} from "../../common/models/celebrity/celebrity";
import {getComparisonWithCelebrity} from "../../api/compareService";
import {AxiosError} from "axios";
import {Comparison} from "../../common/models/compare/comparison.model";
import {QuizType} from "../../common/enums/quizType.enum";
import {useCategoryComparison} from "../../common/hooks/useCategoryComparison";
import {CelebrityType} from "../../common/enums/celebrityType.enum";
import CelebrityIcon from "../../common/icons/tmb/celebrities/celebrity.icon";
import ActorIcon from "../../common/icons/tmb/celebrities/actor.icon";
import KpopIcon from "../../common/icons/tmb/celebrities/kpop.icon";
import AthleteIcon from "../../common/icons/tmb/celebrities/athlete.icon";
import FictionalIcon from "../../common/icons/tmb/celebrities/fictional.icon";
import SingerIcon from "../../common/icons/tmb/celebrities/singer.icon";
import {CelebrityHelper} from "../../common/utils/celebrity.helper";
import {useIsMobile} from "../../common/hooks/useIsMobile";
import SeasonFilter from "../../common/enums/seasonFilter.enum";
import ChristmasHatIcon from "../../common/icons/tmb/celebrities/seasonOverlays/christmasHat.icon";

interface CompatibilityPreviewProps {
    celebrity?: Celebrity;
    isGuest?: boolean;
}
const CompatibilityPreview = ({celebrity: celebrity, isGuest = false}: CompatibilityPreviewProps) => {
    
    const theme = useTheme();
    const isMobile = useIsMobile();
    const { getComparisonThemeColour } = useCategoryComparison();
    const [comparison, setComparison] = useState<Comparison>();
    const [starSign, setStarSign] = useState<string | undefined>(undefined);
    const [celebrityImage, setCelebrityImage] = useState<ReactElement>(<UserProfilePicture size={72} />);
    const [seasonFilter, setSeasonFilter] = useState<boolean>(false);
    const [seasonOverlay, setSeasonOverlay] = useState<ReactElement>(<></>);

    useEffect( () => {
        if(!celebrity)
        {
            return;
        }

        if(!isGuest) {
            getComparisonResultDetails();
        }
        
        if(celebrity.imageUrl)
        {
            setCelebrityImage(
                <img
                    width={72}
                    height={72}
                    style={{ borderRadius: '100%', backgroundSize: 'contain' }}
                    src={celebrity.imageUrl}
                    alt={"Celebrity Image"}
                />
            );
            return;
        }

        switch (celebrity.type) {
            case CelebrityType.CELEBRITY:
                setCelebrityImage(<CelebrityIcon size={72} />);
                break;
            case CelebrityType.ACTOR:
                setCelebrityImage(<ActorIcon size={72} />);
                break;
            case CelebrityType.KPOP:
                setCelebrityImage(<KpopIcon size={72} />);
                break;
            case CelebrityType.ATHLETE:
                setCelebrityImage(<AthleteIcon size={72} />);
                break;
            case CelebrityType.FICTIONAL:
                setCelebrityImage(<FictionalIcon size={72} />);
                break;
            case CelebrityType.SINGER:
                setCelebrityImage(<SingerIcon size={72} />);
                break;
            default:
                setCelebrityImage(<UserProfilePicture size={72} />);
                break;
        }
    }, [celebrity]);

    useEffect(() => {
        setStarSign(comparison?.comparisonResultDetails.results.find(c => c.quizType == QuizType.StarSign)?.result);
    }, [comparison]);

    useEffect(() => {
        switch (EnvironmentConfig.SeasonFilter){
            case SeasonFilter.Christmas: {
                setSeasonFilter(true)
                setSeasonOverlay(<ChristmasHatIcon size={44} style={{position: "absolute", top: -14, left: 30}}/>)
                break;
            }
            default:
                return;
        }
    }, []);

    const getComparisonResultDetails = async ()=> {
        try {
            const response = await getComparisonWithCelebrity(Number(celebrity?.id));

            setComparison(response);
        } catch (error) {
            console.error(error);
            if((error as AxiosError).response?.status === 404) {
                window.location.replace(`/${FixedRoute.Compatibility}`);
            }
        }
    };

    return (
        <Stack className={isGuest ? "guest-compatibility-preview" : "compatibility-preview"} py={2} px={2.5} width={"100%"} borderRadius={"2px"} >
            <Stack gap={2.5} flexDirection={!isMobile ? "row" : "column"} width={'100%'} sx={{textDecoration: "none"}}
                   component={"a"} href={!isGuest && celebrity ? `${EnvironmentConfig.BaseUrl}/${FixedRoute.CompatibilityCelebrity}/${celebrity.id}` : undefined}>
                <Stack alignItems={"center"} flex={1} gap={1}>
                    <UserProfilePicture size={72} />
                    <Typography variant={"body2"} color={theme.palette.thatsMe.textTitle} flex={1} textAlign={"center"} justifyContent={"center"} minWidth={'72px'} >
                        You
                    </Typography>
                </Stack>
                <Typography 
                    variant={"h4"} 
                    color={theme.palette.thatsMe.textTitle} 
                    alignItems={"center"} 
                    justifyContent={"center"} 
                    flex={1}
                    height={72}
                >
                    +
                </Typography>
                <Stack alignItems={"center"} flex={1} gap={1}>
                    <Box position={"relative"}>
                        {seasonFilter && seasonOverlay}
                        {celebrityImage}
                    </Box>
                    <Typography variant={"body2"} color={theme.palette.thatsMe.textTitle} flex={1} textAlign={"center"} justifyContent={"center"} minWidth={'72px'} >
                        {isGuest && celebrity ? CelebrityHelper.formatCelebrityName(celebrity as Celebrity) : comparison?.displayNameAgainst}
                    </Typography>
                </Stack>
                <Typography
                    variant={"h4"}
                    flex={1}
                    justifyContent={"center"}
                    alignItems={"center"}
                    color={isGuest ? theme.palette.thatsMe.textTitle : getComparisonThemeColour(String(starSign),QuizType.StarSign)}
                    height={72}
                >
                    = {isGuest ? <>??</> : starSign}% 
                </Typography>
            </Stack>
        </Stack>
    );
}

export default CompatibilityPreview